import React from 'react';
import { Treatment } from '@/types/components/whatIsYourConcern';
import InfoBox from './InfoBox';
import RichText from '@/components/ui/RichText/RichText';
import R2Image from '@/components/ui/R2Image';

type Props = {
  treatment: Treatment;
  compact?: boolean;
};

type LegacyImageTreatment = {
  imageUrl?: string;
};

type NewImageTreatment = {
  image?: {
    urlDesktop?: string;
    urlMobile?: string;
    alt?: string;
    className?: string;
    width?: number;
    height?: number;
  };
};

const getTreatmentImage = (treatment: Treatment) => {
  const item = treatment as Treatment &
    LegacyImageTreatment &
    NewImageTreatment;

  const imagePath =
    item.image?.urlDesktop || item.image?.urlMobile || item.imageUrl || '';

  const imageAlt =
    item.image?.alt || treatment?.heading?.content || 'Treatment';

  return {
    imagePath,
    imageAlt,
    imageClassName:
      item.image?.className || 'h-full w-full rounded-lg object-cover',
    imageWidth: item.image?.width || 362,
    imageHeight: item.image?.height || 281,
  };
};

const TreatmentCard = ({ treatment, compact = false }: Props) => {
  const { imagePath, imageAlt, imageClassName, imageWidth, imageHeight } =
    getTreatmentImage(treatment);

  const durationDetail = treatment.details?.find((detail) =>
    detail.content.toLowerCase().includes('duration'),
  );

  const downtimeDetail = treatment.details?.find((detail) =>
    detail.content.toLowerCase().includes('downtime'),
  );

  const resultsDetail = treatment.details?.find((detail) =>
    detail.content.toLowerCase().includes('results'),
  );

  const getDetailValue = (detail: any) => {
    if (!detail?.content) return '';

    const parts = detail.content.split('\n\n');

    return parts[parts.length - 1]?.trim() || '';
  };

  return (
    <div
      style={{ boxShadow: '0px 4px 6px 1px #0000001F' }}
      className="w-full overflow-hidden rounded-lg px-4 pt-4"
    >
      {imagePath ? (
        <div className="overflow-hidden rounded-lg">
          <R2Image
            path={imagePath}
            alt={imageAlt}
            width={imageWidth ?? 96}
            height={imageHeight ?? 96}
            className={imageClassName}
          />
        </div>
      ) : null}

      <div className="p-4">
        <RichText
          styles={{
            p: 'text-xl font-bold text-theme-primary [&>p]:mb-0',
            ...treatment.heading.styles,
          }}
          className=""
        >
          {treatment.heading.content}
        </RichText>

        {treatment.description?.content ? (
          <RichText
            styles={{
              p: 'mt-1 text-sm font-bold text-text-placeholder [&>p]:mb-0',
              ...treatment.description.styles,
            }}
            className=""
          >
            {treatment.description.content}
          </RichText>
        ) : null}

        <div className="mt-3 grid min-w-0 grid-cols-3 gap-1.5 sm:gap-2">
          {durationDetail && (
            <InfoBox
              label="Duration"
              value={getDetailValue(durationDetail)}
              icon={
                <R2Image
                  path="/icons/duration.webp"
                  alt="Duration"
                  width={16}
                  height={16}
                  className="h-full w-full"
                />
              }
            />
          )}

          {downtimeDetail && (
            <InfoBox
              label="Downtime"
              value={getDetailValue(downtimeDetail)}
              icon={
                <R2Image
                  path="/icons/down-time.webp"
                  alt="Downtime"
                  width={16}
                  height={16}
                  className="h-full w-full"
                />
              }
            />
          )}

          {resultsDetail && (
            <InfoBox
              label="Results"
              value={getDetailValue(resultsDetail)}
              icon={
                <R2Image
                  path="/icons/skin.webp"
                  alt="Results"
                  width={16}
                  height={16}
                  className="h-full w-full"
                />
              }
            />
          )}
        </div>

        {treatment.frequency?.content ? (
          <RichText
            styles={{
              strong: 'font-bold text-[16px] text-text-brand',
              p: 'text-[14px] font-regular text-text-placeholder-dark mt-1',
            }}
            className="mt-2"
          >
            {treatment.frequency.content}
          </RichText>
        ) : null}

        {treatment.bestSuitedFor?.content ? (
          <RichText
            styles={{
              p: `text-text-default font-regular ${
                compact ? 'text-[14px]' : 'text-[12px]'
              } mb-0`,
              strong: 'font-bold text-[16px] text-text-brand',
              ul: 'list-disc pl-5 text-sm text-default-caption font-bold',
              li: 'mb-1 text-default-caption marker:text-[18px]',
            }}
            className="mt-4"
          >
            {treatment.bestSuitedFor.content}
          </RichText>
        ) : null}
      </div>
    </div>
  );
};

export default TreatmentCard;
